import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Icon from 'components/global/Icon';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, ENUMS, SHIPPING_AUTOMATION } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { DYNAMICLISTS } from 'utils/constants';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

/**
 * @author @omerrkosar
 */

const ReturnTable = ({ isLoading, fetchAutocompleteData }) => {
	const { control, setValue, watch, trigger } = useFormContext();
	const { fields, append, remove, move } = useFieldArray({
		control,
		name: 'actions'
	});
	const value = watch('priorityMethods');

	const deliveryCompanyColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const isMobile = useMediaQuery(Breakpoints.mobile);

	const columnsPercentageDistribute = useMemo(
		() => [
			{
				id: 'deliveryCompany',
				header: 'deliveryCompany',
				accessor: 'deliveryCompany',
				size: isMobile ? 40 : 120,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						name={`actions[${index}].deliveryCompanySettingsID`}
						value={watch(`actions[${index}].deliveryCompanySettingsID`)}
						placeholder="search"
						columns={deliveryCompanyColumns}
						selectionLabelAccessor={['Name']}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;

							setValue(`actions[${index}].deliveryCompanySettingsID`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});

							trigger('actions');
						}}
						fetchFunction={watch('feasibilityRuleID') ? fetchAutocompleteData : undefined}
						autoCompleteName={
							!watch('feasibilityRuleID') ? AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS : undefined
						}
					/>
				)
			},
			{
				header: 'distributionValue',
				accessor: 'distributionValue',
				size: isMobile ? 20 : 40,
				renderCell: ({ row: { index } }) => (
					<IntegerInput
						showArrows={false}
						required
						unit="%"
						name={`actions[${index}].value`}
						value={watch(`actions[${index}].value`)}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[isMobile, value]
	);
	const columnsManualRating = useMemo(
		() => [
			{
				id: 'rank',
				size: 10,
				maxSize: 10,
				header: 'rank',
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<div style={{ display: 'flex' }}>
						<Typography>{index + 1}</Typography>
					</div>
				)
			},
			{
				id: 'deliveryCompanySettingsID',
				header: 'deliveryCompanySetting',
				accessor: 'deliveryCompanySettingsID',
				size: isMobile ? 40 : 120,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						name={`actions[${index}].deliveryCompanySettingsID`}
						value={watch(`actions[${index}].deliveryCompanySettingsID`)}
						placeholder="search"
						columns={deliveryCompanyColumns}
						selectionLabelAccessor={['Name']}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;
							setValue(`actions[${index}].deliveryCompanySettingsID`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});
							setValue(`actions[${index}].value`, index + 1, {
								shouldValidate: true,
								shouldDirty: true
							});

							trigger('actions');
						}}
						fetchFunction={watch('feasibilityRuleID') ? fetchAutocompleteData : undefined}
						autoCompleteName={
							!watch('feasibilityRuleID') ? AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS : undefined
						}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20,
				showOnMobile: true
			}
		],
		[isMobile, value]
	);
	const columnsRest = useMemo(
		() => [
			{
				id: 'deliveryCompany',
				accessor: 'deliveryCompany',
				header: 'deliveryCompany',
				size: isMobile ? 40 : 120,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<AutoComplete
						name={`actions[${index}].deliveryCompanySettingsID`}
						value={watch(`actions[${index}].deliveryCompanySettingsID`)}
						placeholder="search"
						columns={deliveryCompanyColumns}
						selectionLabelAccessor={['Name']}
						setValue={(value, isFirstRender) => {
							if (isFirstRender) return;

							setValue(`actions[${index}].deliveryCompanySettingsID`, value?.ID ?? null, {
								shouldValidate: true,
								shouldDirty: true
							});

							trigger('actions');
						}}
						fetchFunction={watch('feasibilityRuleID') ? fetchAutocompleteData : undefined}
						autoCompleteName={
							watch('feasibilityRuleID') ? undefined : AUTOCOMPLETE.DELIVERY_COMPANY_SETTINGS
						}
					/>
				)
			},
			{
				id: 'deleteRow',
				size: 20,
				maxSize: 20
			}
		],
		[isMobile, value]
	);

	if (value === 'percentageDistribute') {
		return (
			<Table
				key={'table'}
				loading={isLoading}
				data={fields}
				columns={columnsPercentageDistribute}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({ deliveryCompanySettingsID: null, value: 1 });
				}}
				showAddNewRowButton
			/>
		);
	}
	if (value === 'manualRating') {
		return (
			<Table
				key={'table'}
				loading={isLoading}
				data={fields}
				columns={columnsManualRating}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({ deliveryCompanySettingsID: null, value: 1 });
				}}
				showAddNewRowButton
				onRowDrag={move}
			/>
		);
	} else {
		return (
			<Table
				key={'table'}
				loading={isLoading}
				data={fields}
				columns={columnsRest}
				onRowDelete={(row) => {
					remove(row.index);
				}}
				onRowAdd={() => {
					append({ deliveryCompanySettingsID: null, value: 1 });
				}}
				showAddNewRowButton
			/>
		);
	}
};

const PriorityRuleForm = NiceModal.create(({ id, mode, modalName }) => {
	const { t } = useTranslation();
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					ruleName: yup.string().nullable().required(),
					priorityMethods: yup.string().nullable().required(),
					feasibilityRuleID: yup.number().nullable(),
					dataPeriod: yup.number().nullable(),
					status: yup.string().nullable().required(),
					actions: yup
						.array()
						.of(
							yup
								.object()
								.shape({
									deliveryCompanySettingsID: yup
										.number()
										.nullable()
										.when('priorityMethods', ([priorityMethods], schema) => {
											if (
												priorityMethods === 'manualRating' ||
												priorityMethods === 'percentageDistribute'
											) {
												return schema.required();
											} else {
												return schema;
											}
										}),
									value: yup.number().nullable()
								})
								.test('requiredField', t('alert.requiredField'), (value, context) => {
									const method = watch('priorityMethods');
									const id = value?.deliveryCompanySettingsID;

									if (!id && (method === 'manualRating' || method === 'percentageDistribute')) {
										return context.createError({
											path: `${context.path}.deliveryCompanySettingsID`,
											message: t('alert.requiredField'),
											type: 'manual'
										});
									}
									return true;
								})
								.test('manual', t('errorMsg.duplicateDC'), (value, context) => {
									const id = value.deliveryCompanySettingsID;
									const ids = context.parent.map((item) => item.deliveryCompanySettingsID);
									const isValueNotUnique = ids.indexOf(id) !== ids.lastIndexOf(id);

									return isValueNotUnique
										? context.createError({
												path: `${context.path}.deliveryCompanySettingsID`,
												message: t('errorMsg.duplicateDC'),
												type: 'manual'
											})
										: true;
								})
						)
						.nullable()
				})

				.required(),
		[]
	);

	const { showSuccess, showError } = useAlert();
	const [isLoading, setIsLoading] = useState(false);
	const axiosPrivate = useAxiosPrivate();
	const { closeModal } = useModalRouter();
	const currentPlanName = useSelector((state) => state.user.planName);
	const [enumName, setEnumName] = useState(ENUMS.PRIORITY_METHODS_SCALE);
	const [isMinimizing, setIsMinimizing] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		if (currentPlanName === 'ScalePackage') setEnumName(ENUMS.PRIORITY_METHODS_SCALE);
		if (currentPlanName === 'EnterprisePackage') setEnumName(ENUMS.PRIORITY_METHODS_ENTERPRISE);
	}, [currentPlanName]);

	const dispatch = useDispatch();

	// Form config
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			ruleName: null,
			priorityMethods: null,
			feasibilityRuleID: null,
			dataPeriod: 1,
			status: null,
			actions: []
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		setError,
		reset,
		clearErrors,
		formState: { isDirty, isSubmitSuccessful, errors, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting && !isMinimizing);

	const deliveryCompanyColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		await axiosPrivate
			.post(SHIPPING_AUTOMATION.GET_PRIORITY_RULE, { ID: id })
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					reset({
						ID: data?.ID ?? null,
						ruleName: data.name ?? null,
						feasibilityRuleID: data.feasibilityRuleID ?? null,
						dataPeriod: data.dataPeriod ?? 1,
						priorityMethods: data.method ?? null,
						status: data.status ?? null,
						actions:
							data?.actions?.map((action) => ({
								deliveryCompanySettingsID: action.deliveryCompanySettingsID,
								value: action?.value ?? 1
							})) ?? 1
					});
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
		setIsLoading(false);
	}, [id]);

	useEffect(() => {
		if (id) {
			fillForm();
		}
	}, [id]);

	const onSubmit = (data) => {
		const url = id ? SHIPPING_AUTOMATION.UPDATE_PRIORITY_RULE : SHIPPING_AUTOMATION.ADD_PRIORITY_RULE;
		setIsLoading(true);

		axiosPrivate
			.post(url, {
				ID: id,
				name: data.ruleName,
				feasibilityRuleID: data.feasibilityRuleID,
				dataPeriod: data.dataPeriod,
				method: data.priorityMethods,
				status: data.status,
				actions: data?.actions
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.PRIORITY_RULES));
					closeModal();
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				if (error.response.data.code === 'error.SA002') {
					showError({ message: 'error.SA002', header: error.response.data.message });
					setError('feasibilityRuleID', {
						type: 'manual',
						message: 'error.SA002'
					});
				}
				if (error.response.data.code === 'error.SA006') {
					showError({ message: 'error.SA006', header: error.response.data.message });
					setError('feasibilityRuleID', {
						type: 'manual',
						message: 'error.SA006'
					});
				} else {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
			});

		setIsLoading(false);
	};

	const defaultColSpan = {
		md: 6
	};

	const fetchAutocompleteData = useCallback(
		async (props) => {
			const ruleID = watch('feasibilityRuleID');

			const data = await axiosPrivate
				.post(SHIPPING_AUTOMATION.GET_FEASIBILITY_RULE_DCS_BY_RULEID, {
					ruleID
				})
				.then((response) => {
					const { data } = response;

					return data.data;
				})
				.catch((error) => {
					AppLogger.error(error);
				});
			return data?.filter((item) =>
				props?.keyword
					? item.name.toLowerCase().includes(props?.keyword.toLowerCase())
					: props?.IDs
						? props?.IDs.includes(item.ID)
						: true
			);
		},
		[watch('feasibilityRuleID')]
	);

	const priorityMethods = watch('priorityMethods');

	return (
		<Modal
			fullwidth
			maxWidth="medium"
			name={modalName}
			className="modal-priorityRuleForm"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={id ? 'editPriorityRule' : 'addPriorityRule'}
					tooltip={id ? watch('ruleName') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{id ? 'editPriorityRule' : 'addPriorityRule'}</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="ruleName" isLoading={isLoading} required>
						<StringInput name="ruleName" />
					</FormItem>
					<FormItem
						colSpan={defaultColSpan}
						label="method"
						isLoading={isLoading}
						required
						customLabelContent={
							currentPlanName === 'EnterprisePackage' ? null : (
								<Tooltip
									timeout={100}
									placement="right"
									color={'var(--white)'}
									title={
										<div>
											<ModuleToolTipContainer
												lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/border/upgradeRocket.json.gz"
												title="shippingAutomationModuleConditionTitle"
												description="shippingAutomationModuleConditionSubtitle"
											/>

											<Button
												className="mt-3 mx-auto"
												label="upgradePlan"
												variant="blue"
												onClick={() => navigate('/oto-modules/advance-shipping-automation')}
											/>
										</div>
									}>
									<Icon icon={faCircleInfo} />
								</Tooltip>
							)
						}>
						<EnumSelect
							name="priorityMethods"
							placeholder="selectOne"
							enumName={enumName}
							setValue={(value) => {
								setValue('priorityMethods', value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(
									'actions',
									value === 'manualRating' ? [{ deliveryCompanySettingsID: null, value: 1 }] : []
								);
								clearErrors('actions');
							}}
							value={priorityMethods}
						/>
					</FormItem>

					{(priorityMethods === 'slaMet' ||
						priorityMethods === 'customerRating' ||
						priorityMethods === 'deliveryTime' ||
						priorityMethods === 'deliveryRate') && (
						<FormItem colSpan={defaultColSpan} label="dataPeriod" isLoading={isLoading}>
							<IntegerInput showArrows={false} unit="months" name="dataPeriod" />
						</FormItem>
					)}

					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading} required>
						<EnumSelect
							name="status"
							placeholder="selectOne"
							enumName={ENUMS.FEASIBILITY_STATUS}
							setValue={(value) => {
								setValue('status', value, { shouldValidate: true, shouldDirty: true });
							}}
							value={watch('status')}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="feasibilityRule" isLoading={isLoading}>
						<AutoComplete
							name="feasibilityRuleID"
							value={watch(`feasibilityRuleID`)}
							placeholder="search"
							columns={deliveryCompanyColumns}
							selectionLabelAccessor={['Name']}
							setValue={async (value, isFirstRender) => {
								if (isFirstRender) return;
								setValue(`feasibilityRuleID`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
								!id &&
									setValue(
										'actions',
										value === 'manualRating' ? [{ deliveryCompanySettingsID: null, value: 1 }] : []
									);
							}}
							autoCompleteName={AUTOCOMPLETE.FEASIBILITY_RULES_ACTIVE}
						/>
					</FormItem>

					{(priorityMethods === 'manualRating' || priorityMethods === 'percentageDistribute') && (
						<FormItem label="actions" required className="table">
							<ReturnTable fetchAutocompleteData={fetchAutocompleteData} isLoading={isLoading} />
						</FormItem>
					)}
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={closeModal} />
				<Button
					label={mode === 'edit' ? 'save' : 'button.add'}
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					disabled={!isDirty || errors.actions}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					iconPos="right"
				/>
			</ModalActions>
		</Modal>
	);
});

export default PriorityRuleForm;
